import { getConfig } from "config";
import { HttpError } from "./httpError";
import { buildUrl, QueryParamValue } from "./url";
import { withSpan } from "utils/observability";

type LockerRequestOptions = {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  params?: Record<string, QueryParamValue>;
  body?: unknown;
  headers?: HeadersInit;
  cache?: RequestCache;
  signal?: AbortSignal;
  errorMessage?: string | ((response: Response) => string);
};

const buildHeaders = (apiKey: string, body: unknown, extraHeaders?: HeadersInit) => {
  const headers: Record<string, string> = {
    "X-Locker-Private-Key": apiKey,
  };

  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  return { ...headers, ...(extraHeaders ?? {}) } as HeadersInit;
};

export const requestLockerJson = async <T>(
  path: string,
  options: LockerRequestOptions = {}
): Promise<T> => {
  const { apiUrl, apiKey } = getConfig();
  const url = buildUrl(apiUrl, path, options.params ?? {});
  const method = options.method ?? "GET";

  return withSpan(
    {
      op: "http.client",
      name: `${method} ${path}`,
      attributes: {
        "http.method": method,
        "http.url": url,
      },
    },
    async (span) => {
      const response = await fetch(url, {
        method,
        cache: options.cache,
        signal: options.signal,
        headers: buildHeaders(apiKey, options.body, options.headers),
        body: options.body ? JSON.stringify(options.body) : undefined,
      });

      span?.setAttribute("http.status_code", response.status);

      if (!response.ok) {
        const message =
          typeof options.errorMessage === "function"
            ? options.errorMessage(response)
            : (options.errorMessage ?? "Network Error");
        throw new HttpError(message, response, url);
      }

      return response.json();
    }
  );
};

export const requestLockerVoid = async (
  path: string,
  options: LockerRequestOptions = {}
): Promise<void> => {
  const { apiUrl, apiKey } = getConfig();
  const url = buildUrl(apiUrl, path, options.params ?? {});
  const method = options.method ?? "POST";

  await withSpan(
    {
      op: "http.client",
      name: `${method} ${path}`,
      attributes: {
        "http.method": method,
        "http.url": url,
      },
    },
    async (span) => {
      const response = await fetch(url, {
        method,
        cache: options.cache,
        signal: options.signal,
        headers: buildHeaders(apiKey, options.body, options.headers),
        body: options.body ? JSON.stringify(options.body) : undefined,
      });

      span?.setAttribute("http.status_code", response.status);

      if (!response.ok) {
        const message =
          typeof options.errorMessage === "function"
            ? options.errorMessage(response)
            : (options.errorMessage ?? "Network Error");
        throw new HttpError(message, response, url);
      }
    }
  );
};

export type { QueryParamValue, LockerRequestOptions };
